import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { IAgentsRaw, ICreateNotificationStateObj } from '@/api/types';
import type { RootState } from '@/store';
import type { ICreateNotificationState, IScheduleOptions, IScheduleRaw, IThunkAPI, scheduleObj } from '@/store/types';
import type { ScheduleId } from '@/types/schedule';

import { notifyApi } from '@/api/su/notify';

const notificationStateInitialValue: ICreateNotificationState = {
  scheduleId: 0,
  agentsRequired: 0,
  dateStart: null,
  dateEnd: null,
  description: '',
  customText: '',
  internalNotes: '',
  useVoice: false,
  useSms: false,
  useMail: false,
};

const initialState: ICreateNotificationStateObj = {
  clientViseScheduleList: null,
  isScheduleListError: false,
  isScheduleListSuccess: false,
  isScheduleListFetching: false,
  createNotificationState: notificationStateInitialValue,
  clientViseAgentsList: null,
  notificationCreated: false,
};

interface IScheduleResponseObj {
  clientId: number;
  data: IScheduleRaw[];
}

export const getSchudulelistByClientId = createAsyncThunk<IScheduleResponseObj, number, IThunkAPI>(
  'get/notification/su/scheduleList',
  async (id, thunkAPI) => {
    try {
      const response = await notifyApi.getSchudulelistByClientId(id);
      return { clientId: id, data: response.data };
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const setNotifyPreview = createAsyncThunk<
  { clientId: number; data: IAgentsRaw[] },
  { scheduleId: ScheduleId; startDate: string | null; endDate: string | null; clientId: number },
  IThunkAPI
>('get/notification/su/agentsList', async ({ scheduleId, startDate, endDate, clientId }, thunkAPI) => {
  try {
    const response = await notifyApi.setNotifyPreview({
      scheduleId,
      startDate: startDate ? new Date(startDate) : new Date(),
      endDate: endDate ? new Date(endDate) : new Date(),
    });
    return { clientId: clientId, data: response.data };
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getNotifyJobCreate = createAsyncThunk<
  unknown,
  { payload: ICreateNotificationState; timeZone: string },
  IThunkAPI
>('get/notification/su/getNotifyJobCreate', async ({ payload, timeZone }, thunkAPI) => {
  try {
    const response = await notifyApi.getNotifyJobCreate(payload, timeZone);
    return response;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const createNotificationSlice = createSlice({
  name: 'createNotification',
  initialState,
  reducers: {
    reSetCreateNotificationState(state) {
      state.createNotificationState = notificationStateInitialValue;
    },
    updateCreateNotificationState(state, { payload }) {
      state.createNotificationState = { ...payload };
    },
    updateNotificationCreatedStatus(state, { payload }) {
      state.notificationCreated = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getSchudulelistByClientId.fulfilled, (state, action) => {
        const scheduleListOptions = [];
        const scheduleListObj: scheduleObj = {};

        for (let i = 0; i < action?.payload?.data?.length; i++) {
          const item = action?.payload?.data[i];
          const id = item?.scheduleId || 0;
          const name = item?.scheduleName || '';

          scheduleListOptions.push({
            value: id,
            label: name,
          });
          scheduleListObj[id] = name;
        }

        if (action?.payload?.clientId) {
          state.clientViseScheduleList = {
            ...state.clientViseScheduleList,
            [action.payload.clientId]: { scheduleList: scheduleListOptions, scheduleListObj: scheduleListObj },
          };
        }

        state.isScheduleListSuccess = true;
        state.isScheduleListError = false;
        state.isScheduleListFetching = false;
      })
      .addCase(getSchudulelistByClientId.rejected, state => {
        state.isScheduleListError = true;
        state.isScheduleListSuccess = false;
        state.isScheduleListFetching = false;
      })
      .addCase(getSchudulelistByClientId.pending, state => {
        state.isScheduleListFetching = true;
      })
      .addCase(setNotifyPreview.fulfilled, (state, action) => {
        if (action?.payload?.clientId) {
          state.clientViseAgentsList = {
            ...state.clientViseAgentsList,
            [action.payload.clientId]: { agentsList: action.payload.data },
          };
        }
      })
      .addCase(getNotifyJobCreate.fulfilled, state => {
        state.notificationCreated = true;
      });
  },
});

// actions
export const { reSetCreateNotificationState, updateCreateNotificationState, updateNotificationCreatedStatus } =
  createNotificationSlice.actions;

// selectors

export const getCreateNotificationState = (state: RootState): ICreateNotificationStateObj['createNotificationState'] =>
  state.createNotification.createNotificationState;

export const getScheduleName = (state: RootState, clientId: number | undefined, scheduleId: ScheduleId): string => {
  let scheduleObj = null;
  let scheduleName = '';
  if (
    clientId &&
    state.createNotification.clientViseScheduleList &&
    state.createNotification.clientViseScheduleList[clientId]
  ) {
    scheduleObj = state.createNotification.clientViseScheduleList[clientId];
    if (scheduleObj && scheduleObj.scheduleListObj && scheduleObj.scheduleListObj[scheduleId]) {
      scheduleName = scheduleObj.scheduleListObj[scheduleId];
    }
  }
  return scheduleName;
};
export const getScheduleOptions = (state: RootState, clientId: number | undefined): IScheduleOptions[] => {
  let scheduleList: IScheduleOptions[] = [];

  if (
    clientId &&
    state.createNotification.clientViseScheduleList &&
    state.createNotification.clientViseScheduleList[clientId] &&
    state.createNotification.clientViseScheduleList[clientId].scheduleList
  ) {
    scheduleList = state.createNotification.clientViseScheduleList[clientId].scheduleList || [];
  }

  return scheduleList;
};
export const getAgentslist = (state: RootState, clientId: number | undefined): IAgentsRaw[] => {
  let agentsList: IAgentsRaw[] = [];
  if (
    clientId &&
    state.createNotification.clientViseAgentsList &&
    state.createNotification.clientViseAgentsList[clientId]
  ) {
    agentsList = state.createNotification.clientViseAgentsList[clientId].agentsList || [];
  }
  return agentsList;
};
export const getNotificationCreatedStatus = (state: RootState): ICreateNotificationStateObj['notificationCreated'] =>
  state.createNotification.notificationCreated;
export const getIsScheduleListFetched = (state: RootState): ICreateNotificationStateObj['isScheduleListFetching'] =>
  state.createNotification.isScheduleListFetching;

export default createNotificationSlice.reducer;
